import {
    type Accommodation,
    accommodationFactory,
    accommodationZodObject,
} from "@@/shared/acquire/accommodation";
import {
    type Bookable,
    bookableFactory,
    bookableZodObject,
} from "@@/shared/acquire/bookable";
import {
    type Buyable,
    buyableFactory,
    buyableZodObject,
} from "@@/shared/acquire/buyable";
import {
    type GiftCard,
    giftCardFactory,
    giftCardZodObject,
} from "@@/shared/acquire/giftcard";
import { entityStateZodSchema } from "@@/shared/entity-status";
import {
    type MetaContent,
    metaContentFactory,
    metaContentZodSchema,
} from "@@/shared/meta-content/meta-content";
import {
    type AvailableQuantity,
    availableQuantityZodSchema,
} from "@@/shared/units-and-quantities/quantity";
import { assertTypeMatch } from "@@/utilities/type-helpers";
import type { Except, IsEqual } from "type-fest";
import { z } from "zod";
import { hardCodedGlobalAccommodationQuantity } from "../acquire/accommodation-quantity";
import type { AcquireType } from "../acquire/acquirable";
import { defaultAccommodationSettings } from "../acquire/bookable-settings";
import { orderConfirmationOption } from "../acquire/order-confirmation-options";
import { genericResourceReferenceFactory } from "../acquire/resource-references/resource-reference-template";
import {
    type Voucher,
    voucherFactory,
    voucherZodObject,
} from "../acquire/voucher";
import { assertNever } from "../assert-never";
import { hourMinuteFactory } from "../date-time/hour-minute";
import type {
    DurationInDays,
    DurationInMinutes} from "../date-time/time-spans";
import {
    inHours,
} from "../date-time/time-spans";
import { zeroPrice } from "../prices/price";
import { translation } from "../translations/translation";
import { amountFactory } from "../units-and-quantities/amount";
import { units } from "../units-and-quantities/unit";
import type { ProductAddCommand } from "./commands/product-add-command";
import { type ProductId, productIdZodSchema } from "./product-id";
import { type SkuId, skuIdFactory, skuIdZodSchema } from "./sku-id";

const _skuBaseZodObject = z.object({
    _id: skuIdZodSchema,
    _type: z.literal("SKU"),
    _status: entityStateZodSchema,
    productId: productIdZodSchema,
    meta: metaContentZodSchema,
    articleNumber: z.string().optional(),
    availableQuantity: availableQuantityZodSchema,
});

type _SkuBase = z.infer<typeof _skuBaseZodObject>;
const giftCardSkuZodObject = _skuBaseZodObject.merge(
    z.object({ acquire: giftCardZodObject }),
);
type GiftCardSku = z.infer<typeof giftCardSkuZodObject>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isGiftCardSku = (sku?: any): sku is GiftCardSku =>
    sku?._type === "SKU" && sku.acquire._type === "GIFTCARD";
const bookableSkuZodObject = _skuBaseZodObject.merge(
    z.object({ acquire: bookableZodObject }),
);
type BookableSku = z.infer<typeof bookableSkuZodObject>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isBookableSku = (sku?: any): sku is BookableSku =>
    sku?._type === "SKU" && sku.acquire._type === "BOOKABLE";
const buyableSkuZodObject = _skuBaseZodObject.merge(
    z.object({
        acquire: buyableZodObject,
        trackStockBalance: z.boolean().optional(),
    }),
);
type BuyableSku = z.infer<typeof buyableSkuZodObject>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isBuyableSku = (sku?: any): sku is BuyableSku =>
    sku?._type === "SKU" && sku.acquire._type === "BUYABLE";
const voucherSkuZodObject = _skuBaseZodObject.merge(
    z.object({
        acquire: voucherZodObject,
        trackStockBalance: z.boolean().optional(),
    }),
);
type VoucherSku = z.infer<typeof voucherSkuZodObject>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isVoucherSku = (sku?: any): sku is VoucherSku =>
    sku?._type === "SKU" && sku.acquire._type === "VOUCHER";
const accommodationSkuZodObject = _skuBaseZodObject.merge(
    z.object({ acquire: accommodationZodObject }),
);
type AccommodationSku = z.infer<typeof accommodationSkuZodObject>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isAccommodationSKu = (sku?: any): sku is AccommodationSku =>
    sku?._type === "SKU" && sku.acquire._type === "ACCOMMODATION";
const skuZodUnion = z.union([
    giftCardSkuZodObject,
    bookableSkuZodObject,
    buyableSkuZodObject,
    voucherSkuZodObject,
    accommodationSkuZodObject,
]);

type SkuTypeMap = {
    GIFTCARD: GiftCardSku;
    BOOKABLE: BookableSku;
    BUYABLE: BuyableSku;
    VOUCHER: VoucherSku;
    ACCOMMODATION: AccommodationSku;
};

// Make sure we've got all type types of `AcquireType`
// covered as keys in the `SkuTypeMap`
assertTypeMatch<IsEqual<keyof SkuTypeMap, AcquireType>>();
// And that all types in `SkuTypeMap` are covered by the `skuZodUnion`
// and export a type of the union without using zod's `infer` keyword
// to prevent to large types
type Sku = SkuTypeMap[keyof SkuTypeMap];

type CreateSkuBaseProps = Except<
    Sku,
    "_id" | "meta" | "_type" | "_status" | "availableQuantity"
> & {
    availableQuantity?: AvailableQuantity;
};

const _skuBaseFactory = (
    params: CreateSkuBaseProps & {
        _id?: SkuId;
        productId: string;
        meta?: MetaContent;
        availableQuantity?: number | AvailableQuantity;
    },
): Sku => {
    return {
        ...params,
        _id: params._id || skuIdFactory(),
        _type: "SKU",
        _status: "ACTIVE",
        availableQuantity: params.availableQuantity ?? "INFINITE",
        meta: metaContentFactory({
            title: params.meta?.title || "",
            ...params.meta,
        }),
    };
};

const buyableSkuFactory = (
    params: Except<CreateSkuBaseProps, "acquire"> & {
        _id?: SkuId;
        productId: ProductId;
        acquire: Buyable;
        meta?: MetaContent;
    },
): BuyableSku => {
    const base = _skuBaseFactory(params);
    return { ...base, acquire: params.acquire };
};

const voucherSkuFactory = (
    params: Except<CreateSkuBaseProps, "acquire"> & {
        _id?: SkuId;
        productId: ProductId;
        acquire: Voucher;
        meta?: MetaContent;
    },
): VoucherSku => {
    const base = _skuBaseFactory(params);
    return { ...base, acquire: params.acquire };
};

const giftCardSkuFactory = (
    params: Except<CreateSkuBaseProps, "acquire"> & {
        _id?: SkuId;
        productId: ProductId;
        acquire: GiftCard;
        meta?: MetaContent;
    },
): GiftCardSku => {
    const base = _skuBaseFactory(params);
    return { ...base, acquire: params.acquire };
};
const bookableSkuFactory = (
    params: Except<CreateSkuBaseProps, "acquire"> & {
        _id?: SkuId;
        productId: ProductId;
        meta?: MetaContent;
        acquire: Bookable;
    },
): BookableSku => {
    const base = _skuBaseFactory(params);
    return { ...base, acquire: params.acquire };
};

const accommodationSkuFactory = (
    params: Except<CreateSkuBaseProps, "acquire"> & {
        _id?: SkuId;
        productId: ProductId;
        meta?: MetaContent;
        acquire: Accommodation;
    },
): AccommodationSku => {
    const base = _skuBaseFactory(params);
    return { ...base, acquire: params.acquire };
};
type HasStockBalance = BuyableSku | VoucherSku;

const skuFactory = {
    from: <T extends Accommodation | Bookable | Buyable | GiftCard | Voucher>(
        params: Except<CreateSkuBaseProps, "acquire"> & {
            _id?: SkuId;
            productId: ProductId;
            meta?: MetaContent;
            acquire: T;
        },
    ): Sku => {
        const { acquire, ...rest } = params;
        switch (acquire._type) {
            case "ACCOMMODATION":
                return accommodationSkuFactory({
                    ...rest,
                    acquire,
                });
            case "BOOKABLE":
                return bookableSkuFactory({
                    ...rest,
                    acquire,
                });
            case "BUYABLE":
                return buyableSkuFactory({
                    ...rest,
                    acquire,
                });
            case "GIFTCARD":
                return giftCardSkuFactory({
                    ...rest,
                    acquire,
                });
            case "VOUCHER":
                return voucherSkuFactory({
                    ...rest,
                    acquire,
                });
            default:
                assertNever(acquire);
        }
    },
    fromProductAddCommand: <AcquirableAs extends keyof SkuTypeMap>(
        command: ProductAddCommand,
    ): SkuTypeMap[AcquirableAs] => {
        switch (command.acquirableAs) {
            case "BOOKABLE": {
                return bookableSkuFactory({
                    acquire: bookableFactory({
                        price: zeroPrice,
                        amount: amountFactory({
                            unit: units[command.amount.unit.type],
                            value: command.amount.value,
                            quantity: 1,
                        }),
                        resources: [],
                        extras: [],
                        extrasGroups: [],
                        sessions: undefined,
                        contentBlocks: [],
                        main: genericResourceReferenceFactory(),
                        settings: {
                            postTime: 0 as DurationInMinutes,
                            prepTime: 0 as DurationInMinutes,
                            allowBookingDuringSession: false,
                            orderConfirm: orderConfirmationOption.AUTO,
                        },
                        type: command.bookableType,
                        hasAccess: false,
                    }),
                    articleNumber: command.articleNumber ?? undefined,
                    productId: command.productId,
                }) as SkuTypeMap[AcquirableAs];
            }
            case "BUYABLE": {
                return buyableSkuFactory({
                    acquire: buyableFactory({
                        price: command.price,
                        amount: amountFactory({
                            unit: units[command.amount.unit.type],
                            value: command.amount.value,
                            quantity: command.amount.quantity,
                        }),
                    }),
                    articleNumber: command.articleNumber ?? undefined,
                    productId: command.productId,
                }) as SkuTypeMap[AcquirableAs];
            }
            case "VOUCHER": {
                return voucherSkuFactory({
                    acquire: voucherFactory({
                        price: command.price,
                        amount: amountFactory({
                            unit: units[command.amount.unit.type],
                            value: command.amount.value,
                            quantity: command.amount.quantity,
                        }),
                        valid: inHours({ days: 365 as DurationInDays }),
                        contentBlocks: [],
                    }),
                    articleNumber: command.articleNumber ?? undefined,
                    productId: command.productId,
                }) as SkuTypeMap[AcquirableAs];
            }
            case "GIFTCARD": {
                return giftCardSkuFactory({
                    acquire: giftCardFactory({
                        price: command.price,
                        amount: amountFactory({
                            unit: units[command.amount.unit.type],
                            value: command.amount.value,
                            quantity: command.amount.quantity,
                        }),
                        valid: inHours({ days: 365 as DurationInDays }),
                        contentBlocks: [],
                    }),
                    articleNumber: command.articleNumber ?? undefined,
                    productId: command.productId,
                }) as SkuTypeMap[AcquirableAs];
            }
            case "ACCOMMODATION": {
                return accommodationSkuFactory({
                    acquire: accommodationFactory({
                        price: zeroPrice,
                        amount: amountFactory({
                            unit: units[command.amount.unit.type],
                            value: command.amount.value,
                            quantity: 1,
                        }),
                        quantities: {
                            ADULT: hardCodedGlobalAccommodationQuantity.ADULT,
                            CHILD: hardCodedGlobalAccommodationQuantity.CHILD,
                            INFANT: hardCodedGlobalAccommodationQuantity.INFANT,
                        },
                        inventory: [],
                        rates: [],
                        availability: {
                            availabilityRanges: [],
                            baseSettings: {
                                minimumStay: 1,
                                maximumStay: 31,
                                checkInTime: hourMinuteFactory({
                                    hour: 15,
                                    minute: 0,
                                }),
                                checkOutTime: hourMinuteFactory({
                                    hour: 11,
                                    minute: 0,
                                }),
                                settings: defaultAccommodationSettings,
                            },
                        },
                        extrasGroups: [],
                        contentBlocks: [],
                        quantityDescriptions: {
                            title: translation({
                                sv: "Antal gäster",
                                en: "Number of guests",
                            }),
                            description: translation({
                                sv: "Hur många kommer ni vara? Ange antal personer för att se till att ert boende rymmer er alla bekvämt.",
                                en: "How many will you be? Enter the number of people to make sure your accommodation fits you all comfortably.",
                            }),
                        },
                        hasAccess: false,
                    }),
                    articleNumber: command.articleNumber ?? undefined,
                    productId: command.productId,
                }) as SkuTypeMap[AcquirableAs];
            }
            default: {
                assertNever(command.acquirableAs);
            }
        }
    },
};

// type Sku = {
//     readonly _id: SkuId;
//     readonly _type: "SKU";
//     readonly _status: EntityState;
//     readonly productId: ProductId;
//     readonly meta: MetaContent;
//     readonly acquire: Acquirable; //Buyable | Bookable | Voucher | GiftCard;
//     readonly articleNumber?: string;
//     readonly availableQuantity: AvailableQuantity;
//     readonly trackStockBalance?: boolean;
// };
// const skuZodSchema: z.ZodSchema<Sku> = z.object({
//     _id: skuIdZodSchema,
//     _type: z.literal("SKU"),
//     _status: entityStateZodSchema,
//     productId: productIdZodSchema,
//     meta: metaContentZodSchema,
//     acquire: acquirableZodUnion,
//     articleNumber: z.string().optional(),
//     availableQuantity: availableQuantityZodSchema,
// });

export {
    accommodationSkuFactory,
    accommodationSkuZodObject,
    bookableSkuFactory,
    bookableSkuZodObject,
    buyableSkuFactory,
    buyableSkuZodObject,
    giftCardSkuFactory,
    giftCardSkuZodObject,
    isAccommodationSKu,
    isBookableSku,
    isBuyableSku,
    isGiftCardSku,
    isVoucherSku,
    skuFactory,
    skuZodUnion as skuZodObject,
    voucherSkuFactory,
    voucherSkuZodObject,
};
export type {
    AccommodationSku,
    BookableSku,
    BuyableSku,
    GiftCardSku,
    HasStockBalance,
    Sku,
    SkuTypeMap,
    VoucherSku,
};
